import React, { ComponentProps, useCallback } from "react";

import { ButtonProps, Button as MuiButton, css, styled } from "@mui/material";

import { SvgIcon } from "@/components/ui/svg-icon/SvgIcon";
import { returnOnCondition } from "@/utils/conditionalOutput";
import { skipForwardProp } from "@/utils/skipForwardProp";
import { clsxm } from "@/utils/tailwindMerge";

import { ButtonVariant, ButtononHoverVariant, getOnHoverCss } from "./helpers";

export type PropTypes = Omit<ButtonProps, "variant" | "size"> & {
  variant?: ButtonVariant;
  size?: "small" | "medium" | "large";
  children?: React.ReactNode;
  iconSize?: number;
  iconColor?: ComponentProps<typeof SvgIcon>["color"];
  onHoverVariant?: ButtononHoverVariant;
  startIconClassName?: string;
  endIconClassName?: string;
};

const StyledMuiButton = styled(MuiButton, {
  shouldForwardProp: skipForwardProp(["isChildrenPersist", "onHoverVariant"]),
})<PropTypes & { variant?: ButtonVariant } & { isChildrenPersist?: boolean }>(
  ({ isChildrenPersist, onHoverVariant = "default", as, variant, theme }) => {
    return {
      ...css({
        backgroundSize: "200% 100%",
        ".MuiButton-startIcon": {
          marginLeft: "0px",
        },
        // "&:hover": {
        //   backgroundColor: "rgba(82, 209, 218, 0.04)",
        // },

        ...returnOnCondition(
          !isChildrenPersist,
          {
            "& .MuiButton-startIcon": {
              marginRight: 0,
            },
          },
          {},
        ),

        ...getOnHoverCss(onHoverVariant, variant),

        ...returnOnCondition(
          as === "a",
          {
            display: "inline-flex",
            alignItems: "center",
            color: "green",
            cursor: "pointer",
            "&:hover": {
              backgroundColor: "unset",
              color: theme.palette.green.main,
            },
          },
          {},
        ),
      }),
    };
  },
);

export const Button = React.forwardRef<HTMLButtonElement, PropTypes>(
  (
    {
      variant = "primary",
      size = "medium",
      className,
      children,
      startIcon,
      startIconClassName,
      endIcon,
      endIconClassName,
      iconSize: iconSizeProp,
      iconColor,
      onHoverVariant = "default",
      ...rest
    },
    ref,
  ) => {
    const StartIcon = useCallback(() => {
      if (!startIcon) return null;

      const colors: ComponentProps<typeof SvgIcon>["color"] = iconColor
        ? iconColor
        : variant === "primary"
        ? "white"
        : variant === "secondary"
        ? "green"
        : undefined;

      const iconSize: number | undefined = iconSizeProp
        ? iconSizeProp
        : size === "medium"
        ? 24
        : size === "large"
        ? 24
        : size === "small"
        ? 20
        : undefined;

      return (
        <SvgIcon
          height={iconSize}
          width={iconSize}
          color={colors}
          className={startIconClassName}
        >
          {startIcon}
        </SvgIcon>
      );
    }, [startIcon, iconColor, variant, iconSizeProp, size, startIconClassName]);
    const EndIcon = useCallback(() => {
      if (!endIcon) return null;

      const colors: ComponentProps<typeof SvgIcon>["color"] = iconColor
        ? iconColor
        : variant === "primary"
        ? "white"
        : variant === "secondary"
        ? "green"
        : undefined;

      const iconSize: number | undefined = iconSizeProp
        ? iconSizeProp
        : size === "medium"
        ? 24
        : size === "large"
        ? 24
        : size === "small"
        ? 20
        : undefined;

      return (
        <SvgIcon
          height={iconSize}
          width={iconSize}
          color={colors}
          className={endIconClassName}
        >
          {endIcon}
        </SvgIcon>
      );
    }, [endIcon, iconColor, variant, iconSizeProp, size, endIconClassName]);

    const sharedClasses = clsxm(
      "rounded-2xl font-primary whitespace-nowrap text-base shadow font-bold py-0 px-3",
    );

    const variantClasses: Record<typeof variant, string> = {
      primary: "text-white bg-gradient-green-blue-green",
      secondary: "text-green-600 bg-white",
      default: "text-white bg-gradient-red-pink-red",
      text: "shadow-none p-0 min-w-0 h-min text-grey1-900 normal-case font-normal",
      outlined: "border-green-600 text-green-600 bg-white",
      rounded: "rounded-full p-0 min-w-0 w-8 h-8",
    };

    const sizesClasses: Record<typeof size, string> = {
      large: "h-[48px]",
      medium: "h-8",
      small: "h-6 text-sm px-2",
    };

    const concatedClassNames = clsxm(
      sharedClasses,
      sizesClasses[size],
      variantClasses[variant],
      className,
    );
    const isChildrenPersist = !!children;

    return (
      <StyledMuiButton
        isChildrenPersist={isChildrenPersist}
        ref={ref}
        className={concatedClassNames}
        startIcon={startIcon ? <StartIcon /> : undefined}
        endIcon={endIcon ? <EndIcon /> : undefined}
        onHoverVariant={onHoverVariant}
        variant={variant as TODO}
        {...rest}
      >
        {children}
      </StyledMuiButton>
    );
  },
);

Button.displayName = "Button";
